import type {
  ComposerReply,
  PersonalComposer,
  SaveComposerFull,
} from "../../src/personal-composer-contract.ts";
import {
  type ComposerHead,
  ComposerRequestError,
  type PersonalComposerClient,
} from "./personal-composer-client.ts";
import {
  type ComposerLocalBase,
  type ComposerWords,
  composerWords,
  nonemptyComposerWords,
  sameComposerWords,
  unsentWords,
} from "./personal-composer-content.ts";
import type { UnsentReply } from "./unsent-reply.ts";

export type ComposerSessionState = {
  mode: "loading" | "enabled" | "disabled" | "error";
  saving: boolean;
  dirty: boolean;
  conflict: ComposerHead | null;
  conflictKind: "composer_changed" | "stale_thread" | null;
  error: string;
  /** The server holds words for this box at the accepted revision (a saved draft). */
  stored: boolean;
};

type SessionPorts = {
  changed(state: ComposerSessionState): void;
  /** The saved words, and the whole reply when the server keeps one (absent from an older server). */
  hydrate(composer: PersonalComposer, reply: ComposerReply | null | undefined): void;
  acknowledge(words: ComposerWords | null, revision: number, threadVersion: string): void;
};

type PendingWrite = { words: ComposerWords | null; input: SaveComposerFull };

/**
 * The head after a save: the server's composer, and the reply it now keeps. An older server keeps
 * none and never says so, so the head keeps not knowing.
 */
function savedHead(
  head: ComposerHead,
  composer: PersonalComposer,
  words: ComposerWords | null,
): ComposerHead {
  if (head.reply === undefined) return { ...head, composer };
  return { ...head, composer, reply: words ? (words.reply ?? head.reply) : null };
}

/** One serialized writer per open conversation; failed requests retain their original retry key. */
export class PersonalComposerSession {
  state: ComposerSessionState = {
    mode: "loading",
    saving: false,
    dirty: false,
    conflict: null,
    conflictKind: null,
    error: "",
    stored: false,
  };
  private head: ComposerHead | null = null;
  private words: ComposerWords | null;
  private edited = false;
  private retry: PendingWrite | null = null;
  private writing: Promise<void> | null = null;
  private initializing: Promise<void> | null = null;
  private sent: { id: string; words: ComposerWords; revision: number | undefined } | null = null;

  constructor(
    private readonly client: PersonalComposerClient,
    private readonly local: UnsentReply | null,
    private readonly ports: SessionPorts,
    private readonly initialSourceMessageId: string | null,
  ) {
    this.words = local ? unsentWords(local) : null;
  }

  private patch(next: Partial<ComposerSessionState>): void {
    this.state = { ...this.state, ...next };
    this.ports.changed(this.state);
  }

  start(): Promise<void> {
    this.initializing ??= this.loadInitial();
    return this.initializing;
  }

  private localThreadChanged(head: ComposerHead): boolean {
    if (!this.local) return this.initialSourceMessageId !== head.sourceMessageId;
    const version = this.local.composerThreadVersion;
    return (
      ((this.local.composerManaged === true || this.local.composerRevision !== undefined) &&
        version === undefined) ||
      (version !== undefined && version !== head.threadVersion) ||
      this.local.messageId !== head.sourceMessageId
    );
  }

  private async loadInitial(): Promise<void> {
    try {
      const head = await this.client.read();
      this.head = head;
      this.patch({ mode: "enabled", error: "" });
      const remote = composerWords(head.composer, head.reply);
      if (!this.edited && !this.local) return this.accept(head);
      if (sameComposerWords(this.words, remote)) return this.accept(head);
      if (this.localThreadChanged(head)) {
        this.patch({ conflict: head, conflictKind: "stale_thread", dirty: true });
        return;
      }
      const base = this.local?.composerRevision;
      if (base === head.composer.revision || (base === undefined && head.composer.revision === 0)) {
        this.patch({ dirty: true });
        return;
      }
      this.patch({ conflict: head, conflictKind: "composer_changed", dirty: true });
    } catch (cause) {
      if (cause instanceof ComposerRequestError && cause.code === "mcp_disabled") {
        this.patch({ mode: "disabled", error: "" });
        return;
      }
      this.failed();
    }
  }

  edit(words: ComposerWords): ComposerLocalBase | undefined {
    const next = nonemptyComposerWords(words);
    // A change that leaves the box as it was (a restored reply rendering, a typed-then-undone
    // character) is not an edit to save.
    const same = sameComposerWords(next, this.words);
    this.words = next;
    this.edited = true;
    if (!same || !this.head) this.patch({ dirty: true });
    if (this.state.mode === "disabled") return undefined;
    if (!this.head || this.state.conflict) return { composerManaged: true };
    return {
      composerManaged: true,
      composerRevision: this.head.composer.revision,
      composerThreadVersion: this.head.threadVersion,
    };
  }

  private accept(head: ComposerHead): void {
    this.head = head;
    this.words = composerWords(head.composer, head.reply);
    this.edited = false;
    this.retry = null;
    this.patch({
      dirty: false,
      conflict: null,
      conflictKind: null,
      error: "",
      mode: "enabled",
      stored: head.composer.content !== null,
    });
    this.ports.hydrate(head.composer, head.reply);
    this.ports.acknowledge(this.words, head.composer.revision, head.threadVersion);
  }

  async refresh(): Promise<void> {
    if (this.state.mode === "disabled" || this.writing || this.retry) return;
    const before = this.head;
    try {
      const head = await this.client.read();
      // A read started before a save or another read cannot move the accepted base backwards.
      if (this.head !== before || this.writing || this.retry) return;
      if (!this.head) {
        this.initializing = null;
        await this.start();
        return;
      }
      if (head.composer.revision !== this.head.composer.revision) {
        if (this.state.dirty || this.retry)
          this.patch({ conflict: head, conflictKind: "composer_changed" });
        else this.accept(head);
      } else if (
        head.threadVersion !== this.head.threadVersion &&
        (this.state.dirty || this.retry)
      ) {
        this.patch({ conflict: head, conflictKind: "stale_thread" });
      } else {
        this.head = head;
        this.patch({ mode: "enabled", error: "" });
      }
    } catch {
      this.failed();
    }
  }

  private failed(): void {
    this.patch({
      mode: "error",
      error: "Your draft could not be saved. Your words are still here. Retry before sending.",
    });
  }

  private mutation(words: ComposerWords | null): PendingWrite {
    if (!this.head) throw new Error("Your saved draft is still loading. Try again.");
    return {
      words,
      input: {
        expectedRevision: this.head.composer.revision,
        threadVersion: this.head.threadVersion,
        sourceMessageId: this.head.sourceMessageId,
        idempotencyKey: crypto.randomUUID(),
        format: words?.format ?? "text",
        plainText: words?.plainText ?? "",
        html: words?.html ?? "",
        fromDraft: words?.fromDraft ?? null,
        ...(words?.authored === undefined ? {} : { authored: words.authored }),
        ...(words?.reply ? { reply: words.reply } : {}),
      },
    };
  }

  private async writeOnce(pending: PendingWrite): Promise<void> {
    try {
      const result = pending.words
        ? await this.client.save(pending.input)
        : await this.client.clear({
            expectedRevision: pending.input.expectedRevision,
            threadVersion: pending.input.threadVersion,
            sourceMessageId: pending.input.sourceMessageId,
            idempotencyKey: pending.input.idempotencyKey,
          });
      this.retry = null;
      if (!result.isCurrent) {
        this.patch({
          conflict: await this.client.read(),
          conflictKind: "composer_changed",
          dirty: true,
        });
        return;
      }
      if (this.head) this.head = savedHead(this.head, result.composer, pending.words);
      this.ports.acknowledge(pending.words, result.savedRevision, pending.input.threadVersion);
      this.patch({
        mode: "enabled",
        error: "",
        dirty: !sameComposerWords(this.words, pending.words),
        stored: result.composer.content !== null,
      });
    } catch (cause) {
      if (
        cause instanceof ComposerRequestError &&
        ["composer_changed", "stale_thread", "source_message_not_found"].includes(cause.code)
      ) {
        this.retry = null;
        try {
          this.patch({
            conflict: await this.client.read(),
            conflictKind: cause.code === "composer_changed" ? "composer_changed" : "stale_thread",
            dirty: true,
          });
        } catch {
          this.failed();
        }
        return;
      }
      this.failed();
    }
  }

  async flush(): Promise<void> {
    await this.start();
    if (this.state.mode === "disabled") return;
    if (this.writing) {
      await this.writing;
      return this.flush();
    }
    if (this.state.conflict || !this.head)
      throw new Error("Choose which draft to keep before sending.");
    if (!this.state.dirty && !this.retry) return;
    this.retry ??= this.mutation(this.words);
    this.patch({ saving: true });
    this.writing = this.writeOnce(this.retry);
    await this.writing;
    this.writing = null;
    this.patch({ saving: false });
    if (this.state.error || this.state.conflict)
      throw new Error(this.state.error || "Choose which draft to keep before sending.");
    if (this.state.dirty) await this.flush();
  }

  async keepLocal(): Promise<void> {
    if (!this.state.conflict) return;
    this.head = this.state.conflict;
    this.retry = null;
    this.patch({ conflict: null, conflictKind: null, mode: "enabled", dirty: true, error: "" });
    await this.flush();
  }

  async recover(): Promise<void> {
    if (this.retry) return this.flush();
    await this.refresh();
    await this.flush();
  }

  chooseSaved(): void {
    if (this.state.conflict) this.accept(this.state.conflict);
  }

  async prepare(id: string, words: ComposerWords): Promise<number | undefined> {
    await this.start();
    if (this.state.mode === "disabled") {
      this.sent = { id, words, revision: undefined };
      return undefined;
    }
    if (this.state.conflict) throw new Error("Choose which draft to keep before sending.");
    if (
      !sameComposerWords(
        nonemptyComposerWords(words),
        this.head ? composerWords(this.head.composer, this.head.reply) : null,
      )
    )
      this.edit(words);
    await this.flush();
    if (this.state.mode !== "enabled" || !this.head)
      throw new Error(this.state.error || "Your draft could not be saved. Retry before sending.");
    const revision = this.head.composer.revision;
    this.sent = { id, words, revision };
    return revision;
  }

  async confirmed(id: string, current: ComposerWords): Promise<boolean> {
    await this.start();
    if (this.state.mode === "disabled")
      return this.sent?.id !== id || sameComposerWords(current, this.sent.words);
    if (this.sent?.id !== id) {
      await this.refresh();
      return false;
    }
    const captured = this.sent;
    this.sent = null;
    if (!sameComposerWords(current, captured.words)) {
      await this.refresh();
      return false;
    }
    try {
      const head = await this.client.read();
      if (this.head && head.composer.revision < this.head.composer.revision) return false;
      if (sameComposerWords(this.words, captured.words)) this.accept(head);
      else this.patch({ conflict: head, conflictKind: "composer_changed", dirty: true });
    } catch {
      this.failed();
    }
    return false;
  }
}
